// ══════════════════════════════════════════════════════════════════════════════════════════════
//  ESTAS DENTRO DE OTRA CLINICA — el aviso que no se puede quitar mientras dure
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  QUE ES. Cuando alguien de soporte entra en una clinica cliente (`services/api`, la cabecera
//  `X-Clinica-Id`), TODA la aplicacion pasa a enseñar los datos de esa clinica: sus pacientes, su
//  agenda, sus cobros, su papelera. Y desde el 3-oct-2026 puede cambiarlos como la administracion de esa
//  clinica (el dueño: «también modificar, por tema de soporte»): lo que cambia queda registrado con su nombre,
//  y lo que no puede hacerse en nombre de la clinica lo sigue cortando el servidor (services/soporteSoloLee,
//  LISTA_CERRADA_VISITA). Esta es la banda que lo cuenta, y el sitio desde el que se sale.
//
//  ── UNA FRANJA FINA QUE SIEMPRE SE VE, Y QUE NO TAPA NADA (3-oct-2026) ──────────────────────
//  El fallo que hay que impedir no es «no se entera de que ha entrado»: es «se le olvida a los veinte
//  minutos y toca datos creyendo que son de otro sitio». Por eso la banda no se cierra mientras dure la
//  visita y lleva el NOMBRE DE LA CLINICA. Hasta el 3-oct era una tarjeta con el texto entero pegada
//  arriba DENTRO de la zona que se desplaza (`position: sticky`, z-index 60). El dueño, con el móvil:
//  «el mensaje rojo que sale arriba en el móvil se queda siempre sticky, y no me deja ver la pantalla ni
//  trabajar». MEDIDO con el navegador de verdad: a 390×844 ocupaba 420 px (la mitad de la pantalla), a
//  360×640 el 72 %, en el escritorio 204 px; por encima del menú lateral (que vale 50) y de los botones de
//  la agenda y de la ficha, y a 360 ni se llegaba a pulsar «Panel de administración».
//  AHORA: una franja de UNA línea que va FUERA de la zona que se desplaza, entre la cabecera y el
//  contenido (la montan los cuatro layouts ahí, no dentro del `<main>`). Así siempre está a la vista y
//  EMPUJA el contenido en vez de taparlo: no tiene `position` ni z-index, no puede ponerse encima del
//  menú, de la cabecera ni de un botón. En el móvil dice «Soporte de Biango en X · Salir»; en el
//  escritorio, la frase de siempre en una línea. El texto entero (qué puede y qué no puede hacer) se
//  DESPLIEGA con su botón, y desplegado también empuja (con tope de alto y su propio desplazamiento).
//
//  ── LOS DOS ESTADOS, Y LOS DOS SON INFORMACION ──────────────────────────────────────────────
//   · DENTRO → «estas dentro de X como soporte de Biango». Con el boton de SALIR, que es inmediato: al
//     pulsarlo, la siguiente peticion ya sale a la clinica propia (no hay nada cacheado que la retenga),
//     se cierra la sesion de soporte en el servidor —la clinica deja de ver que hay alguien dentro— y se
//     vuelve a Superadmin, a la lista de clinicas, que es de donde se entro (B-998, 1-oct-2026).
//   · TERMINADO → el acceso ha caducado o se lo han cortado MIENTRAS estaba dentro. Lo detecta
//     `services/api` en el 403 del servidor, y aqui se dice con las palabras del servidor: lo que
//     hay en pantalla son datos viejos de una clinica en la que ya no se entra. Sin este estado, la
//     aplicacion se quedaria pintando lo de antes y soltando errores rojos sin explicacion, que es
//     justo lo que la regla de la casa prohibe. Este sale DESPLEGADO: hay que leerlo para seguir.
//  Y CUANDO NO HAY NI UNA COSA NI LA OTRA NO SE PINTA NADA: una banda vacia permanente es la forma
//  mas rapida de enseñar a la gente a no mirar esta franja.
//
//  ── EL ESTADO NO VIVE AQUI ──────────────────────────────────────────────────────────────────
//  Vive en `services/api`, que es quien pone la cabecera: si viviera aqui habria dos verdades sobre
//  «en que clinica estoy» y un dia dirian cosas distintas. Este componente lo LEE en el primer
//  pintado y se suscribe para repintarse cuando cambie.
import { useState, useEffect } from 'react'
import { estadoDeVisita, suscribirVisita, salirDeClinicaCerrandoSesion } from '../services/api'
import { useIsMobile } from '../hooks/useIsMobile'
import { Ico } from './ui'
import { t, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// El corte de esta banda. Con nombre para que la guardia pueda pintar justo a los dos lados y no a
// ojo: por debajo (el móvil), la franja corta «Soporte de Biango en X · Salir»; por encima, la frase
// entera en una línea.
export const CORTE_BANDA = 900
// A donde se vuelve al salir: el sitio desde el que se entra.
export const AL_SALIR = '/superadmin/clinicas'
// (5-oct-2026) LA DEMO COMO SU DIRECCIÓN. En una clínica de demostración, Superadmin no entra de visita: abre una sesión de
// su dirección y guarda la de Biango en esta clave (services/sesion · CLAVE_SESION_BIANGO, services/demoComoDireccion).
// Mientras esté guardada, esta misma franja lo dice y lleva «Volver a Biango» (sin import: los cuatro layouts ya montan
// esta banda, y lo de volver se carga al pulsar).
export const CLAVE_DEMO_COMO_DIRECCION = 'biango_sesion_guardada'
function demoComoDireccionGuardada() {
  try {
    const g = JSON.parse(window.localStorage.getItem(CLAVE_DEMO_COMO_DIRECCION) || 'null')
    return g && g.token && g.user ? g : null
  } catch (_) { return null }
}

export default function DentroDeClinica() {
  const [estado, setEstado] = useState(() => estadoDeVisita())
  useEffect(() => suscribirVisita(setEstado), [])
  const movil = useIsMobile(CORTE_BANDA)
  // null = lo de fábrica: plegada dentro, desplegada al terminar (ver arriba). Lo demás, lo que pulse la persona.
  const [desplegada, setDesplegada] = useState(null)

  const [volviendo, setVolviendo] = useState(false)
  const dentro = estado && estado.dentro
  const fin = estado && estado.fin
  const demo = !dentro && !fin ? demoComoDireccionGuardada() : null
  // (5-oct-2026) MIENTRAS SE ESTÁ EN LA DEMO, LA SESIÓN DE BIANGO GUARDADA SE RENUEVA (services/demoComoDireccion ·
  // mantenerLaDeBiango): si no, a las 12 h caducaba y «Volver a Biango» mandaba a entrar sin decir por qué.
  const enDemo = !!demo
  useEffect(() => {
    if (!enDemo) return undefined
    let parar = null, vivo = true
    const arrancar = async () => {
      try {
        const m = await import('../services/demoComoDireccion')
        if (vivo) parar = m.mantenerLaDeBiango()
      } catch (_) { /* sin renovación: al volver, si caducó, se dice */ }
    }
    arrancar()
    return () => { vivo = false; if (parar) parar() }
  }, [enDemo])
  // (6-oct-2026) TU NIVEL DE SOPORTE: lo que puedes hacer dentro depende de él (1 solo mira, 2 lo de siempre, 3 como su
  // administración). Lo dice el servidor (GET /acceso-clinica/mi-nivel, sin la cabecera de la clínica, con sus textos); se
  // pide al entrar y, mientras no llega, la banda dice lo de siempre. Lo que de verdad decide es el servidor en cada
  // petición (services/soporteSoloLee): esto solo lo cuenta. Va detrás de los hooks de antes, para no moverles el orden.
  const [nivel, setNivel] = useState(null)
  const idDentro = dentro ? dentro.id : null
  useEffect(() => {
    if (!idDentro) { setNivel(null); return undefined }
    let vivo = true
    const pedir = async () => {
      try {
        const mod = await import('../services/api')
        const api = mod.default
        const r = await api.get('/acceso-clinica/mi-nivel')
        if (vivo && r && r.data && [1, 2, 3].includes(Number(r.data.nivel))) {
          setNivel(r.data)
          // Para las pantallas que no esconden nada al nivel 3 (services/api · soporteConAccesoTotal).
          if (typeof mod.apuntarNivelDeLaVisita === 'function') mod.apuntarNivelDeLaVisita(idDentro, Number(r.data.nivel))
        }
      } catch (_) { /* sin nivel: la banda dice lo de siempre, y el servidor decide igual */ }
    }
    pedir()
    return () => { vivo = false }
  }, [idDentro])
  const numNivel = nivel ? Number(nivel.nivel) : null
  if (demo) {
    const nombreDemo = String((demo.demo && demo.demo.nombre) || t('components.DentroDeClinica.la_clinica_de_demostracion', 'la clínica de demostración'))
    return (
      <div data-demo-como-direccion role="region" aria-label={t('components.DentroDeClinica.estas_en_como_su_direccion', 'Estás en {{nombreDemo}} como su dirección', { nombreDemo })}
        style={{ flexShrink: 0, background: 'color-mix(in srgb, var(--accent) 10%, var(--surface))',
          borderBottom: '1px solid color-mix(in srgb, var(--accent) 40%, var(--border))', boxShadow: 'inset 3px 0 0 var(--accent)', color: 'var(--text-primary)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: movil ? '8px' : '10px', minHeight: movil ? '36px' : '34px',
          padding: '3px calc(' + (movil ? 12 : 24) + 'px + env(safe-area-inset-right, 0px)) 3px calc(' + (movil ? 12 : 24) + 'px + env(safe-area-inset-left, 0px))' }}>
          <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: movil ? '12.5px' : '13px' }}>
            {movil ? <>{t('components.DentroDeClinica.demo', 'Demo: ')}<b>{nombreDemo}</b>{t('components.DentroDeClinica.como_su_direccion', ' como su dirección')}</> : <>{t('components.DentroDeClinica.estas_en', 'Estás en ')}<b>{nombreDemo}</b>{t('components.DentroDeClinica.como_su_direccion_es_una', ' como su dirección: es una clínica de demostración y nada sale de ella')}</>}
          </span>
          <button type="button" data-volver-a-biango disabled={volviendo}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', flexShrink: 0, padding: movil ? '5px 10px' : '4px 11px', minHeight: '28px',
              borderRadius: '7px', fontSize: '12.5px', fontWeight: 800, cursor: volviendo ? 'default' : 'pointer', whiteSpace: 'nowrap',
              border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff' }}
            onClick={async () => {
              setVolviendo(true)
              try {
                const m = await import('../services/demoComoDireccion')
                const { default: api } = await import('../services/api')
                const { avisar } = await import('../context/DialogosContext')
                // Si la sesión de Biango ha caducado, lo dice («Tu sesión de Biango ha caducado: vuelve a entrar») y lleva a entrar.
                await m.volverABiango(api, { avisar })
              } catch (_) { try { window.location.href = AL_SALIR } catch (__) { /* sin ventana */ } }
            }}>
            <Ico n="logout" s={14} c="#fff" /> {volviendo ? t('components.DentroDeClinica.volviendo', 'Volviendo…') : t('components.DentroDeClinica.volver_a_biango', 'Volver a Biango')}
          </button>
        </div>
      </div>
    )
  }
  if (!dentro && !fin) return null

  const nombre = String((dentro || fin).nombre || '').toUpperCase()
  const tono = dentro ? 'var(--danger)' : 'var(--warning)'
  const abierta = desplegada === null ? !dentro : desplegada
  const idDetalle = 'dentro-de-clinica-detalle'
  const lado = movil ? 12 : 24

  // EN FLUJO, SIN `position` NI z-index: es lo que la hace empujar y no tapar (ver arriba). Los lados respetan la muesca
  // del teléfono tumbado, igual que la cabecera que tiene encima.
  const banda = {
    flexShrink: 0,
    background: 'color-mix(in srgb, ' + tono + ' 12%, var(--surface))',
    borderBottom: '1px solid color-mix(in srgb, ' + tono + ' 45%, var(--border))',
    boxShadow: 'inset 3px 0 0 ' + tono,
    color: 'var(--text-primary)',
  }
  const linea = {
    display: 'flex', alignItems: 'center', gap: movil ? '8px' : '10px', minHeight: movil ? '36px' : '34px',
    padding: '3px calc(' + lado + 'px + env(safe-area-inset-right, 0px)) 3px calc(' + lado + 'px + env(safe-area-inset-left, 0px))',
  }
  const frase = { flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: movil ? '12.5px' : '13px', lineHeight: 1.3 }
  const boton = (fuerte) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px', flexShrink: 0,
    padding: movil ? '5px 10px' : '4px 11px', minHeight: '28px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 800,
    cursor: 'pointer', whiteSpace: 'nowrap',
    border: '1px solid ' + (fuerte ? tono : 'color-mix(in srgb, ' + tono + ' 40%, var(--border))'),
    background: fuerte ? tono : 'transparent', color: fuerte ? '#fff' : 'var(--text-primary)',
  })
  const detalle = {
    padding: '2px calc(' + lado + 'px + env(safe-area-inset-right, 0px)) 10px calc(' + (lado + (movil ? 22 : 24)) + 'px + env(safe-area-inset-left, 0px))',
    fontSize: '12.5px', lineHeight: 1.55, color: 'var(--text-secondary)',
    maxHeight: 'calc(var(--alto-pantalla) * 0.4)', overflowY: 'auto',
  }

  const titulo = dentro
    ? (movil
        ? <>{t('components.DentroDeClinica.soporte_de_biango_en', 'Soporte de Biango en ')}<b style={{ color: tono }}>{nombre}</b></>
        : <>{t('components.DentroDeClinica.estas_dentro_de', 'Estás dentro de ')}<b style={{ color: tono }}>{nombre}</b>{t('components.DentroDeClinica.como_soporte_de_biango_lo', ' como soporte de Biango: lo que cambies queda registrado con tu nombre')}</>)
    : <>{t('components.DentroDeClinica.se_ha_terminado_tu_acceso', 'Se ha terminado tu acceso a ')}<b style={{ color: tono }}>{nombre}</b></>
  // (6-oct-2026) TU NIVEL, en una pastilla al lado de la frase (sin nivel todavía, o con el 2, que es lo de siempre, no sale).
  const pastillaNivel = numNivel === 1
    ? { corto: t('components.DentroDeClinica.nivel_1_solo_mirar', 'Nivel 1 · solo mirar'), largo: t('components.DentroDeClinica.nivel_1_solo_puedes_mirar', 'Nivel 1: solo puedes mirar, no se guarda ningún cambio') }
    : numNivel === 3
      ? { corto: t('components.DentroDeClinica.nivel_3_acceso_total', 'Nivel 3 · acceso total'), largo: t('components.DentroDeClinica.nivel_3_acceso_total_puedes', 'Nivel 3: acceso total, puedes hacer lo mismo que su administración') }
      : null

  return (
    <div data-dentro-de-clinica={dentro ? 'dentro' : 'terminado'}
      data-forma={movil ? 'linea' : 'fila'}
      data-clinica={String((dentro || fin).id)}
      role="region" aria-label={dentro ? t('components.DentroDeClinica.estas_dentro_de_como_soporte', 'Estás dentro de {{nombre}} como soporte de Biango', { nombre }) : t('components.DentroDeClinica.se_ha_terminado_tu_acceso_2', 'Se ha terminado tu acceso a {{nombre}}', { nombre })}
      style={banda}>
      <div style={linea}>
        <Ico n="warn" s={movil ? 15 : 16} c={tono} />
        <span style={frase} data-banda-texto
          title={dentro ? t('components.DentroDeClinica.estas_dentro_de_como_soporte_2', 'Estás dentro de {{nombre}} como soporte de Biango: lo que cambies queda registrado con tu nombre', { nombre }) : undefined}>
          {titulo}
        </span>
        {dentro && pastillaNivel && (
          <span data-nivel-soporte={numNivel} title={pastillaNivel.largo}
            style={{ flexShrink: 0, padding: '2px 8px', borderRadius: '999px', fontSize: '11.5px', fontWeight: 800, whiteSpace: 'nowrap',
              border: '1px solid ' + tono, color: tono, background: 'var(--surface)' }}>
            {movil ? pastillaNivel.corto : pastillaNivel.largo}
          </span>
        )}
        <button type="button" data-banda-desplegar aria-expanded={abierta ? 'true' : 'false'} aria-controls={idDetalle}
          aria-label={movil ? (abierta ? t('components.DentroDeClinica.ocultar_el_detalle', 'Ocultar el detalle') : t('components.DentroDeClinica.leer_el_texto_entero', 'Leer el texto entero')) : undefined}
          title={abierta ? t('components.DentroDeClinica.ocultar_el_detalle', 'Ocultar el detalle') : t('components.DentroDeClinica.leer_el_texto_entero', 'Leer el texto entero')}
          onClick={() => setDesplegada(!abierta)} style={boton(false)}>
          {!movil && (abierta ? t('components.DentroDeClinica.ocultar', 'Ocultar') : (dentro ? t('components.DentroDeClinica.que_puedes_hacer', 'Qué puedes hacer') : t('components.DentroDeClinica.por_que', 'Por qué')))}
          <Ico n={abierta ? 'chevronU' : 'chevronD'} s={14} />
        </button>
        {dentro ? (
          <button type="button" data-salir-de-clinica style={boton(true)}
            title={t('components.DentroDeClinica.cerrar_la_sesion_de_soporte', 'Cerrar la sesion de soporte en {{nombre}} y volver a Superadmin. La siguiente peticion ya sale a tu clinica.', { nombre })}
            onClick={() => {
              // Salir es inmediato; cerrar la sesion en el servidor y volver a Superadmin van detras, pase lo que pase.
              const ir = () => { try { window.location.href = AL_SALIR } catch (_) { /* sin ventana (una guardia): ya se ha salido */ } }
              salirDeClinicaCerrandoSesion().then(ir, ir)
            }}>
            <Ico n="logout" s={14} c="#fff" />{t('components.DentroDeClinica.salir', ' Salir')}
          </button>
        ) : (
          <button type="button" data-volver-a-lo-mio style={boton(true)}
            title={t('components.DentroDeClinica.recargar_la_aplicacion_con_tus', 'Recargar la aplicacion con tus propios datos')}
            onClick={() => { window.location.href = '/' }}>
            <Ico n="logout" s={14} c="#fff" /> {movil ? t('components.DentroDeClinica.volver', 'Volver') : t('components.DentroDeClinica.volver_a_mis_datos', 'Volver a mis datos')}
          </button>
        )}
      </div>
      {abierta && (
        <div id={idDetalle} data-banda-detalle style={detalle}>
          {dentro && numNivel === 1 ? (
            <>
              {/* NIVEL 1 · SOLO MIRAR (6-oct-2026). El texto del nivel lo pone el servidor (services/nivelSoporte). */}
              <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{tx('components.DentroDeClinica.estas_dentro_de_como_soporte_3', 'Estás dentro de {{nombre}} como soporte de Biango con el nivel 1: solo puedes mirar.', { nombre })}</div>
              {tx('components.DentroDeClinica.lo_que_intentes_cambiar_te', '{{descripcion}} Lo que intentes cambiar te contestará que tu acceso es de solo lectura y quedará en su Registro de actividad con tu nombre. Su equipo te ve en pantalla y puede cortarlo; la sesión se apaga sola a las ocho horas. Sal en cuanto termines.', { descripcion: nivel.descripcion })}
            </>
          ) : dentro && numNivel === 3 ? (
            <>
              {/* NIVEL 3 · ACCESO TOTAL, COMO SU ADMINISTRACIÓN (6-oct-2026, orden del dueño: sin excepciones). */}
              <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{tx('components.DentroDeClinica.estas_dentro_de_como_soporte_4', 'Estás dentro de {{nombre}} como soporte de Biango con el nivel 3: acceso total, como su administración.', { nombre })}</div>
              {tx('components.DentroDeClinica.no_son_tus_datos_son', '{{descripcion}} No son tus datos: son los pacientes, la agenda, lo clínico y los cobros de esa clínica, y todo lo que guardes, cambies, firmes o borres sale en su Registro de actividad como «Soporte de Biango» con tu nombre. Su equipo te ve en pantalla y puede cortarlo; la sesión se apaga sola a las ocho horas. Sal en cuanto termines.', { descripcion: nivel.descripcion })}
            </>
          ) : dentro ? (
            <>
              <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{tx('components.DentroDeClinica.estas_dentro_de_como_soporte_5', 'Estás dentro de {{nombre}} como soporte de Biango: lo que cambies queda registrado con tu nombre.', { nombre })}</div>
              {t('components.DentroDeClinica.no_son_tus_datos_son_2', 'No son tus datos: son los pacientes, la agenda y los cobros de esa clínica, y lo que guardes, cambies o borres sale en su Registro de actividad como «Soporte de Biango» con tu nombre. Lo clínico no lo escribes tú: tratamientos y odontograma, periodontograma, notas clínicas, historia de salud, medicación y cirugías son de su equipo. Y lo que no puede hacerse en nombre de la clínica (facturas que van a Hacienda, firmas y consentimientos, contraseñas y accesos, sacar o borrar sus datos enteros, migraciones) la aplicación no te lo deja hacer. Su equipo te ve en pantalla y puede cortarlo; la sesión se apaga sola a las ocho horas. Sal en cuanto termines.')}
            </>
          ) : (
            <>
              {/* LAS PALABRAS SON LAS DEL SERVIDOR. Aqui no se adivina si ha caducado, si se lo
                  han cortado o si le han quitado la seccion: eso lo sabe el servidor y lo dice en
                  su respuesta. Lo unico que se añade es la traduccion de UN motivo que el servidor
                  manda aparte (`degradado`) y que su texto no cuenta bien: cuando el panel de
                  plataforma esta en modo averia, la respuesta escrita habla de secciones y quien
                  la lea creeria que le han quitado permisos, cuando lo que pasa es que el panel
                  esta roto y su acceso a la clinica sigue vivo. */}
              {tx('components.DentroDeClinica.lo_que_sigue_en_pantalla', '{{x}} Lo que sigue en pantalla son datos de esa clinica que ya no se pueden recargar.', { x: fin.motivo === 'degradado'
                ? t('components.DentroDeClinica.el_panel_de_plataforma_esta', 'El panel de plataforma esta en modo averia, y mientras dure el servidor no deja entrar en ninguna clinica. Tu acceso no se ha perdido: se podra entrar en cuanto se arregle.')
                : (fin.mensaje || t('components.DentroDeClinica.ya_no_puedes_entrar_en', 'Ya no puedes entrar en los datos de esa clinica.')) })}
            </>
          )}
        </div>
      )}
    </div>
  )
}
